import React, { useState, useEffect } from 'react';
import { useTenant } from '../context/TenantContext';
import patientFieldConfigService, {
  PatientFieldConfig,
  PatientFieldConfigCreate,
  PatientFieldConfigUpdate,
} from '../services/patientFieldConfigService';
import patientStatusConfigService, { PatientStatusConfig } from '../services/patientStatusConfigService';
import patientTypeService, { PatientType } from '../services/patientTypeService';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'bool', label: 'Yes/No' },
  { value: 'single_select', label: 'Single Select' },
];

const labelToKey = (label: string) =>
  label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

export const PatientFieldConfigPage: React.FC = () => {
  const { currentTenant } = useTenant();
  const [configs, setConfigs] = useState<PatientFieldConfig[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [statusOptions, setStatusOptions] = useState<PatientStatusConfig[]>([]);
  const [patientTypes, setPatientTypes] = useState<PatientType[]>([]);
  const [selectedPatientTypeId, setSelectedPatientTypeId] = useState<number | null>(null);
  const [formPatientTypeId, setFormPatientTypeId] = useState<number | null>(null);

  // Form state
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  const [fieldType, setFieldType] = useState('text');
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [newOptionValue, setNewOptionValue] = useState('');
  const [newOptionLabel, setNewOptionLabel] = useState('');
  const [displayInOverview, setDisplayInOverview] = useState(true);
  const [displayInEdit, setDisplayInEdit] = useState(true);
  const [hasDisplayCondition, setHasDisplayCondition] = useState(false);
  const [conditionStatuses, setConditionStatuses] = useState<string[]>([]);
  const [defaultValue, setDefaultValue] = useState<any>('');
  const [editTabName, setEditTabName] = useState('');
  const [editTabOrder, setEditTabOrder] = useState(0);
  const [sortOrder, setSortOrder] = useState(0);
  const [keyManuallyEdited, setKeyManuallyEdited] = useState(false);
  const [isRequired, setIsRequired] = useState(false);
  const [validation, setValidation] = useState<Record<string, any>>({});

  const loadConfigs = React.useCallback(async () => {
    if (!currentTenant) return;
    try {
      setIsLoading(true);
      const [data, statuses, types] = await Promise.all([
        patientFieldConfigService.getAll(currentTenant.id, selectedPatientTypeId ?? undefined),
        patientStatusConfigService.getAll(currentTenant.id),
        patientTypeService.getAll(currentTenant.id),
      ]);
      setConfigs(data);
      setStatusOptions(statuses);
      setPatientTypes(types);
      if (types.length > 0 && selectedPatientTypeId === null) {
        setSelectedPatientTypeId(types[0].id);
        setFormPatientTypeId(types[0].id);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to load patient field configs:', err);
      setError('Failed to load patient field configs');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant, selectedPatientTypeId]);

  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  const resetForm = () => {
    setKey('');
    setLabel('');
    setFieldType('text');
    setOptions([]);
    setNewOptionValue('');
    setNewOptionLabel('');
    setDisplayInOverview(true);
    setDisplayInEdit(true);
    setHasDisplayCondition(false);
    setConditionStatuses([]);
    setDefaultValue('');
    setEditTabName('');
    setEditTabOrder(0);
    setSortOrder(0);
    setKeyManuallyEdited(false);
    setIsRequired(false);
    setValidation({});
    setFormPatientTypeId(selectedPatientTypeId);
  };

  const handleAdd = () => {
    setIsAdding(true);
    setEditingId(null);
    resetForm();
  };

  const handleEdit = (config: PatientFieldConfig) => {
    setEditingId(config.id);
    setIsAdding(false);
    setKey(config.key);
    setLabel(config.label);
    setFieldType(config.field_type);
    setOptions(config.options || []);
    setDisplayInOverview(config.display_in_overview);
    setDisplayInEdit(config.display_in_edit);
    setHasDisplayCondition(!!config.display_condition);
    setConditionStatuses(config.display_condition?.values || []);
    setDefaultValue(config.default_value ?? '');
    setEditTabName(config.edit_tab_name ?? '');
    setEditTabOrder(config.edit_tab_order ?? 0);
    setSortOrder(config.sort_order);
    setKeyManuallyEdited(true);
    setIsRequired(config.is_required ?? false);
    setValidation(config.validation ?? {});
    setFormPatientTypeId(config.patient_type_id);
  };

  const handleSave = async () => {
    if (!currentTenant || !key.trim() || !label.trim()) return;
    if (isAdding && !formPatientTypeId) return;

    const baseData = {
      key,
      label,
      field_type: fieldType,
      options: fieldType === 'single_select' ? options : null,
      display_in_overview: displayInOverview,
      display_in_edit: displayInEdit,
      display_condition: hasDisplayCondition && conditionStatuses.length > 0
        ? { field: 'status', values: conditionStatuses }
        : null,
      default_value: defaultValue !== '' ? defaultValue : null,
      edit_tab_name: displayInEdit && editTabName.trim() ? editTabName.trim() : null,
      edit_tab_order: editTabOrder,
      sort_order: sortOrder,
      is_required: isRequired,
      validation: Object.keys(validation).length > 0 ? validation : null,
    };

    try {
      if (isAdding) {
        const createData: PatientFieldConfigCreate = {
          ...baseData,
          patient_type_id: formPatientTypeId!,
        };
        await patientFieldConfigService.create(currentTenant.id, createData);
      } else if (editingId) {
        const updateData: PatientFieldConfigUpdate = {
          ...baseData,
          ...(formPatientTypeId !== null ? { patient_type_id: formPatientTypeId } : {}),
        };
        await patientFieldConfigService.update(editingId, updateData);
      }
      await loadConfigs();
      handleCancel();
    } catch (err) {
      console.error('Failed to save patient field config:', err);
      setError('Failed to save patient field config');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this field config?')) return;

    try {
      await patientFieldConfigService.delete(id);
      await loadConfigs();
    } catch (err) {
      console.error('Failed to delete patient field config:', err);
      setError('Failed to delete patient field config');
    }
  };

  const handleCancel = () => {
    setIsAdding(false);
    setEditingId(null);
    resetForm();
  };

  const handleAddOption = () => {
    if (!newOptionValue.trim() || !newOptionLabel.trim()) return;
    setOptions([...options, { value: newOptionValue.trim(), label: newOptionLabel.trim() }]);
    setNewOptionValue('');
    setNewOptionLabel('');
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleUpdateOption = (index: number, field: 'value' | 'label', newValue: string) => {
    setOptions(options.map((opt, i) => i === index ? { ...opt, [field]: newValue } : opt));
  };

  const handleLabelChange = (newLabel: string) => {
    setLabel(newLabel);
    if (!keyManuallyEdited) {
      setKey(labelToKey(newLabel));
    }
  };

  const setValidationField = (field: string, value: any) => {
    setValidation(prev => {
      const next = { ...prev };
      if (value === '' || value === null || value === undefined) {
        delete next[field];
      } else {
        next[field] = value;
      }
      return next;
    });
  };

  const handleConditionStatusToggle = (statusValue: string) => {
    setConditionStatuses(prev =>
      prev.includes(statusValue)
        ? prev.filter(s => s !== statusValue)
        : [...prev, statusValue]
    );
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Patient Fields</h1>
          <button
            onClick={handleAdd}
            className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
          >
            + Add Field
          </button>
        </div>

        {/* Patient Type Filter */}
        {patientTypes.length > 0 && (
          <div className="mb-4 flex items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Patient Type:</label>
            <select
              value={selectedPatientTypeId ?? ''}
              onChange={(e) => {
                const val = e.target.value ? Number(e.target.value) : null;
                setSelectedPatientTypeId(val);
                setFormPatientTypeId(val);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">All Types</option>
              {patientTypes.map((pt) => (
                <option key={pt.id} value={pt.id}>{pt.name}</option>
              ))}
            </select>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {error}
          </div>
        )}

        {/* Add/Edit Form */}
        {(isAdding || editingId) && (
          <div className="mb-6 p-4 border border-gray-300 rounded-md bg-gray-50">
            <h3 className="text-lg font-semibold mb-4">
              {isAdding ? 'Add New Field' : 'Edit Field'}
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => handleLabelChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Enter field label"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Key</label>
                <input
                  type="text"
                  value={key}
                  onChange={(e) => { setKey(e.target.value); setKeyManuallyEdited(true); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Auto-generated from label"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Field Type</label>
                <select
                  value={fieldType}
                  onChange={(e) => setFieldType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  {FIELD_TYPES.map((ft) => (
                    <option key={ft.value} value={ft.value}>{ft.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
                <input
                  type="number"
                  value={sortOrder}
                  onChange={(e) => setSortOrder(parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>
              {patientTypes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Patient Type {isAdding && <span className="text-red-500">*</span>}
                  </label>
                  <select
                    value={formPatientTypeId ?? ''}
                    onChange={(e) => setFormPatientTypeId(e.target.value ? Number(e.target.value) : null)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Select type</option>
                    {patientTypes.map((pt) => (
                      <option key={pt.id} value={pt.id}>{pt.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* Default Value */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Value <span className="text-gray-400 font-normal">(optional)</span></label>
              {fieldType === 'bool' ? (
                <select
                  value={defaultValue === true ? 'true' : defaultValue === false ? 'false' : ''}
                  onChange={(e) => setDefaultValue(e.target.value === '' ? '' : e.target.value === 'true')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">— No default —</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              ) : fieldType === 'single_select' ? (
                <select
                  value={defaultValue ?? ''}
                  onChange={(e) => setDefaultValue(e.target.value || '')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="">— No default —</option>
                  {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              ) : (
                <input
                  type={fieldType === 'number' ? 'number' : fieldType === 'date' ? 'date' : 'text'}
                  value={defaultValue ?? ''}
                  onChange={(e) => setDefaultValue(fieldType === 'number' ? (e.target.value === '' ? '' : parseFloat(e.target.value)) : e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Leave empty for no default"
                />
              )}
            </div>

            {/* Options (only for single_select) */}
            {fieldType === 'single_select' && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
                {options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={opt.label}
                      onChange={(e) => handleUpdateOption(i, 'label', e.target.value)}
                      placeholder="Label"
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 w-32"
                    />
                    <input
                      type="text"
                      value={opt.value}
                      onChange={(e) => handleUpdateOption(i, 'value', e.target.value)}
                      placeholder="Value"
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 w-32"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(i)}
                      className="text-red-600 hover:text-red-900 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newOptionValue}
                    onChange={(e) => setNewOptionValue(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Value"
                  />
                  <input
                    type="text"
                    value={newOptionLabel}
                    onChange={(e) => setNewOptionLabel(e.target.value)}
                    className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                    placeholder="Label"
                  />
                  <button
                    type="button"
                    onClick={handleAddOption}
                    className="px-3 py-1.5 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}

            {/* Display options */}
            <div className="flex gap-6 mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={displayInOverview}
                  onChange={(e) => setDisplayInOverview(e.target.checked)}
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700">Display in Overview</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={displayInEdit}
                  onChange={(e) => setDisplayInEdit(e.target.checked)}
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700">Display in Edit</span>
              </label>
            </div>

            {/* Edit Tab settings — only when display_in_edit is on */}
            {displayInEdit && (
              <div className="mb-4 p-3 border border-teal-200 rounded-md bg-teal-50">
                <p className="text-sm font-medium text-teal-800 mb-3">Edit Dialog Tab</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tab Name</label>
                    <input
                      type="text"
                      value={editTabName}
                      onChange={(e) => setEditTabName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                      placeholder="e.g. Medical Info"
                    />
                    <p className="text-xs text-gray-500 mt-1">Fields with the same tab name are grouped together</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tab Order</label>
                    <input
                      type="number"
                      value={editTabOrder}
                      onChange={(e) => setEditTabOrder(parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Lower numbers appear first</p>
                  </div>
                </div>
              </div>
            )}

            {/* Display Condition */}
            <div className="mb-4">
              <label className="flex items-center gap-2 mb-2">
                <input
                  type="checkbox"
                  checked={hasDisplayCondition}
                  onChange={(e) => setHasDisplayCondition(e.target.checked)}
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm text-gray-700">Display only for specific statuses</span>
              </label>
              {hasDisplayCondition && (
                <div className="flex flex-wrap gap-2 ml-6">
                  {statusOptions.map((status) => (
                    <label key={status.key} className="flex items-center gap-1">
                      <input
                        type="checkbox"
                        checked={conditionStatuses.includes(status.key)}
                        onChange={() => handleConditionStatusToggle(status.key)}
                        className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                      />
                      <span className="text-sm text-gray-600">{status.title}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Required */}
            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isRequired}
                  onChange={(e) => setIsRequired(e.target.checked)}
                  className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                />
                <span className="text-sm font-medium text-gray-700">Required field</span>
              </label>
            </div>

            {/* Validation Rules */}
            {(fieldType === 'text' || fieldType === 'number' || fieldType === 'date') && (
              <div className="mb-4 p-3 border border-gray-200 rounded-md bg-gray-50">
                <p className="text-sm font-medium text-gray-700 mb-3">Validation Rules <span className="text-gray-400 font-normal">(optional)</span></p>

                {fieldType === 'text' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Min Length</label>
                      <input
                        type="number"
                        min={0}
                        value={validation.min_length ?? ''}
                        onChange={(e) => setValidationField('min_length', e.target.value === '' ? '' : parseInt(e.target.value))}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="e.g. 2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Max Length</label>
                      <input
                        type="number"
                        min={0}
                        value={validation.max_length ?? ''}
                        onChange={(e) => setValidationField('max_length', e.target.value === '' ? '' : parseInt(e.target.value))}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="e.g. 100"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Regex Pattern</label>
                      <input
                        type="text"
                        value={validation.regex ?? ''}
                        onChange={(e) => setValidationField('regex', e.target.value)}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="e.g. ^[0-9]{9}$"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Error Message</label>
                      <input
                        type="text"
                        value={validation.error_message ?? ''}
                        onChange={(e) => setValidationField('error_message', e.target.value)}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="e.g. Must be a 9-digit number"
                      />
                    </div>
                  </div>
                )}

                {fieldType === 'number' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Minimum Value</label>
                      <input
                        type="number"
                        value={validation.min_value ?? ''}
                        onChange={(e) => setValidationField('min_value', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="e.g. 0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Maximum Value</label>
                      <input
                        type="number"
                        value={validation.max_value ?? ''}
                        onChange={(e) => setValidationField('max_value', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="e.g. 120"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Error Message</label>
                      <input
                        type="text"
                        value={validation.error_message ?? ''}
                        onChange={(e) => setValidationField('error_message', e.target.value)}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="e.g. Age must be between 0 and 120"
                      />
                    </div>
                  </div>
                )}

                {fieldType === 'date' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Validation Type</label>
                      <select
                        value={validation.type ?? ''}
                        onChange={(e) => {
                          setValidationField('type', e.target.value);
                          setValidationField('value', '');
                        }}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                      >
                        <option value="">— No validation —</option>
                        <option value="past_date">Past Date</option>
                        <option value="future_date">Future Date</option>
                        <option value="within_x_days">Date Within X Days</option>
                        <option value="min_age">Minimum Age</option>
                      </select>
                    </div>
                    {(validation.type === 'within_x_days' || validation.type === 'min_age') && (
                      <div className="col-span-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {validation.type === 'within_x_days' ? 'Number of Days' : 'Minimum Age (years)'}
                        </label>
                        <input
                          type="number"
                          min={0}
                          value={validation.value ?? ''}
                          onChange={(e) => setValidationField('value', e.target.value === '' ? '' : parseInt(e.target.value))}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                          placeholder={validation.type === 'within_x_days' ? 'e.g. 30' : 'e.g. 18'}
                        />
                      </div>
                    )}
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Error Message</label>
                      <input
                        type="text"
                        value={validation.error_message ?? ''}
                        onChange={(e) => setValidationField('error_message', e.target.value)}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="e.g. Date must be in the past"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Configs Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Key</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Label</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Default</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Overview</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Edit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Order</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {configs.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    No patient fields configured. Click "Add Field" to create one.
                  </td>
                </tr>
              ) : (
                configs.map((config) => (
                  <tr key={config.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{config.key}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{config.label}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {FIELD_TYPES.find(ft => ft.value === config.field_type)?.label || config.field_type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {config.default_value === null || config.default_value === undefined || config.default_value === ''
                        ? <span className="text-gray-400">—</span>
                        : config.field_type === 'bool'
                          ? (config.default_value ? 'Yes' : 'No')
                          : String(config.default_value)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{config.display_in_overview ? 'Yes' : 'No'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{config.display_in_edit ? 'Yes' : 'No'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{config.sort_order}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleEdit(config)}
                        className="text-teal-600 hover:text-teal-900 mr-4"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(config.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
